import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  TutorialId,
  TutorialState,
  TutorialContextValue,
} from '../types/tutorial';
import { usePreferences } from './PreferencesContext';
import { preferencesService } from '../services/preferences';

const defaultState: TutorialState = {
  shown: new Set<string>(),
  skipAll: false,
  currentTutorial: undefined,
  currentStep: 0,
};

const TutorialContext = createContext<TutorialContextValue | undefined>(
  undefined
);

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { preferences } = usePreferences();
  const [state, setState] = useState<TutorialState>(() => defaultState);

  // Load from preferences when they become available
  useEffect(() => {
    if (preferences?.tutorials) {
      setState((prev) => ({
        ...prev,
        shown: new Set(preferences.tutorials?.shown || []),
        skipAll: preferences.tutorials?.skip_all || false,
      }));
    }
  }, [preferences]);

  const startTutorial = useCallback(async (tutorialId: TutorialId) => {
    setState((prev) => ({
      ...prev,
      currentTutorial: tutorialId,
      currentStep: 0,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: prev.currentStep + 1,
    }));
  }, []);

  const previousStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  }, []);

  const skipTutorial = useCallback(() => {
    // Mark as shown in local state only (not synced to backend)
    // This prevents the tutorial from showing again during this session
    // but will show again after page refresh
    const newShown = new Set(state.shown);
    if (state.currentTutorial) {
      newShown.add(state.currentTutorial);
    }

    setState((prev) => ({
      ...prev,
      shown: newShown,
      currentTutorial: undefined,
      currentStep: 0,
    }));
  }, [state.shown, state.currentTutorial]);

  const completeTutorial = useCallback(
    async (tutorialId: TutorialId) => {
      // Mark as shown and save to backend
      const newShown = new Set(state.shown);
      newShown.add(tutorialId);

      setState((prev) => ({
        ...prev,
        shown: newShown,
        currentTutorial: undefined,
        currentStep: 0,
      }));

      // Save to backend
      try {
        await preferencesService.updatePreference(
          'tutorials.shown',
          Array.from(newShown)
        );
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      }
    },
    [state.shown]
  );

  const dismissTutorial = useCallback(
    async (tutorialId: TutorialId) => {
      // Mark as shown and save to backend
      const newShown = new Set(state.shown);
      newShown.add(tutorialId);

      setState((prev) => ({
        ...prev,
        shown: newShown,
        currentTutorial: undefined,
        currentStep: 0,
      }));

      // Save to backend
      try {
        await preferencesService.updatePreference(
          'tutorials.shown',
          Array.from(newShown)
        );
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      }
    },
    [state.shown]
  );

  const resetAllTutorials = useCallback(async () => {
    setState(defaultState);
    try {
      await preferencesService.updatePreference('tutorials', {
        shown: [],
        skip_all: false,
      });
    } catch (error) {
      console.error('Failed to reset tutorials:', error);
    }
  }, []);

  const setSkipAll = useCallback(async (skip: boolean) => {
    setState((prev) => ({
      ...prev,
      skipAll: skip,
      currentTutorial: undefined,
      currentStep: 0,
    }));

    try {
      await preferencesService.updatePreference('tutorials.skip_all', skip);
    } catch (error) {
      console.error('Failed to save skip_all state:', error);
    }
  }, []);

  const isTutorialShown = useCallback(
    (tutorialId: TutorialId) => {
      return state.shown.has(tutorialId);
    },
    [state.shown]
  );

  const value: TutorialContextValue = {
    state,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    dismissTutorial,
    resetAllTutorials,
    setSkipAll,
    isTutorialShown,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorialContext = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error(
      'useTutorialContext must be used within a TutorialProvider'
    );
  }
  return context;
};
